/**
 * Modes screen — search, sort (newest / oldest / popular), and local play-count tracking.
 */

import { MODES_WITH_BEST_LINE, formatModeBestLine, getModeBestKind } from "./mode-records";
import { getPinnedModeIds } from "./mode-pins";
import { syncModeCardMenuButtons } from "./mode-context-menu";
import { fetchModePlayCounts, incrementModePlayCount } from "../lib/cloud";
import { createVisibilityInterval, type VisibilityIntervalHandle } from "../lib/visibility-poll";
import { activateModeFromCard, startModeById, syncModeSelectionHighlight } from "./modes-selection-panel";
import { refreshCustomScrollbars } from "./custom-scrollbar";
import { syncModePlayStreakBadges } from "./play-streaks";
import {
    playChallengeRoulette,
    showChallengeRevealText,
} from "./challenges/roulette-ui";
import type { ChallengeSfxKind } from "./challenges/runtime";

export type ModeSortFilter = "newest" | "oldest" | "popular" | "recent";

type ModeDef = {
    id: string;
    addedOrder: number;
    section: "sp";
    search: string[];
};

const KEY_MODE_PLAY_COUNTS = "modePlayCountsV2";
const KEY_MODE_SORT_FILTER = "modeSortFilterV1";
const KEY_MODE_RECENT_PLAYS = "modeRecentPlaysV1";
const MODE_VIEW_MIN_MS = 3000;
const MODE_RECENT_MAX = 24;

/** Drop legacy per-start counts when switching to 5s view rule. */
try {
    localStorage.removeItem("modePlayCountsV1");
} catch {
    /* ignore */
}

const VALID_MODE_SORT_FILTERS: ModeSortFilter[] = ["newest", "oldest", "popular", "recent"];

const MODE_DEFS: ModeDef[] = [
    { id: "normal", addedOrder: 0, section: "sp", search: ["normal"] },
    { id: "progression", addedOrder: 1, section: "sp", search: ["progression", "prog"] },
    { id: "wasd", addedOrder: 2, section: "sp", search: ["wasd", "keyboard"] },
    { id: "superdot", addedOrder: 3, section: "sp", search: ["superdot", "super dot"] },
    { id: "roulette", addedOrder: 4, section: "sp", search: ["roulette"] },
    { id: "speedrun", addedOrder: 6, section: "sp", search: ["speedrun", "speed run"] },
    { id: "penalty", addedOrder: 19, section: "sp", search: ["golf", "penalty", "football", "soccer", "ball"] },
    { id: "highway", addedOrder: 7, section: "sp", search: ["highway"] },
    { id: "darkness", addedOrder: 8, section: "sp", search: ["darkness"] },
    { id: "mirror", addedOrder: 9, section: "sp", search: ["mirror"] },
    { id: "snake", addedOrder: 10, section: "sp", search: ["snake"] },
    { id: "combo", addedOrder: 11, section: "sp", search: ["combo"] },
    { id: "colorrush", addedOrder: 13, section: "sp", search: ["color rush", "color", "rush"] },
    { id: "heat", addedOrder: 14, section: "sp", search: ["heat", "temperature", "calor"] },
    { id: "hunter", addedOrder: 15, section: "sp", search: ["hunter", "hunt", "red"] },
    { id: "pop", addedOrder: 22, section: "sp", search: ["pop", "split", "divide", "balls"] },
    { id: "counter", addedOrder: 17, section: "sp", search: ["counter", "count", "numbers", "order"] },
    { id: "aim", addedOrder: 18, section: "sp", search: ["aim", "click", "golden", "target"] },
    { id: "hook", addedOrder: 20, section: "sp", search: ["hook", "grapple", "laser", "red"] },
    { id: "tilt", addedOrder: 21, section: "sp", search: ["tilt", "gyro", "motion", "incline", "mobile"] },
    { id: "orbit", addedOrder: 23, section: "sp", search: ["orbit", "circle", "core", "defend", "spin", "rotate"] },
    { id: "delay", addedOrder: 24, section: "sp", search: ["delay", "lag", "ghost", "echo", "replay"] },
];

const MODE_BY_ID = Object.fromEntries(MODE_DEFS.map((m) => [m.id, m])) as Record<string, ModeDef>;

const MODE_DISPLAY_TITLES: Record<string, string> = {
    normal: "Normal",
    progression: "Progression",
    wasd: "WASD",
    superdot: "Superdot",
    roulette: "Roulette",
    speedrun: "SpeedRun",
    penalty: "Golf",
    highway: "Highway",
    darkness: "Darkness",
    mirror: "Mirror",
    snake: "Snake",
    combo: "Combo",
    colorrush: "Color Rush",
    heat: "Heat",
    hunter: "Hunter",
    pop: "POP",
    counter: "Counter",
    aim: "AIM",
    hook: "Hook",
    tilt: "Tilt",
    orbit: "Orbit",
    delay: "Delay",
};

type ModeDescCopy = { pc: string; mobile: string } | string;

export function isModesTouchUi(): boolean {
    if (typeof matchMedia === "undefined") return false;
    if (matchMedia("(pointer: coarse)").matches) return true;
    if (matchMedia("(max-width: 820px)").matches && navigator.maxTouchPoints > 0) return true;
    return false;
}

function resolveModeDesc(desc: ModeDescCopy): string {
    if (typeof desc === "string") return desc;
    return isModesTouchUi() ? desc.mobile : desc.pc;
}

const CURSOR_MOVE_PC = "Move with your cursor.";
const CURSOR_MOVE_MOBILE = "Move with touch.";

const MODE_DESCRIPTIONS: Record<string, ModeDescCopy> = {
    normal: {
        pc: `${CURSOR_MOVE_PC}<br>Balls keep piling on.<br>Every bounce makes them angrier.`,
        mobile: `${CURSOR_MOVE_MOBILE}<br>Balls keep piling on.<br>Every bounce makes them angrier.`,
    },
    progression: {
        pc: `${CURSOR_MOVE_PC}<br>A green safe lane shifts sides.<br>Each level hits harder.`,
        mobile: `${CURSOR_MOVE_MOBILE}<br>A green safe lane shifts sides.<br>Each level hits harder.`,
    },
    wasd: {
        pc: "Move with your Keyboard.<br>Momentum is your ally — don't stall.",
        mobile: "Move with D-PAD.<br>Momentum is your ally — don't stall.",
    },
    superdot: {
        pc: `${CURSOR_MOVE_PC}<br>Shield, slow, barrier, and push — use each wisely.`,
        mobile: `${CURSOR_MOVE_MOBILE}<br>Shield, slow, barrier, and push — use each wisely.`,
    },
    roulette: "Every few seconds, the rules flip.<br>Size, speed, visibility — roll with it.",
    speedrun: {
        pc: `${CURSOR_MOVE_PC}<br>Lasers, walls, and a glowing finish line.`,
        mobile: `${CURSOR_MOVE_MOBILE}<br>Lasers, walls, and a glowing finish line.`,
    },
    penalty: {
        pc: `${CURSOR_MOVE_PC}<br>Stroke the ball through laser fairways — fewest hits wins.`,
        mobile: `${CURSOR_MOVE_MOBILE}<br>Stroke the ball through laser fairways — fewest hits wins.`,
    },
    highway: {
        pc: `${CURSOR_MOVE_PC}<br>The road climbs forever — stay inside the frame.`,
        mobile: `${CURSOR_MOVE_MOBILE}<br>The road climbs forever — stay inside the frame.`,
    },
    darkness: {
        pc: `${CURSOR_MOVE_PC}<br>Light fades around you.<br>Chase sparks before the dark wins.`,
        mobile: `${CURSOR_MOVE_MOBILE}<br>Light fades around you.<br>Chase sparks before the dark wins.`,
    },
    mirror: {
        pc: `${CURSOR_MOVE_PC}<br>A twin mirrors every move — both must stay clean.`,
        mobile: `${CURSOR_MOVE_MOBILE}<br>A twin mirrors every move — both must stay clean.`,
    },
    snake: {
        pc: `${CURSOR_MOVE_PC}<br>Your trail lingers.<br>Red apples drift through the field.`,
        mobile: `${CURSOR_MOVE_MOBILE}<br>Your trail lingers.<br>Red apples drift through the field.`,
    },
    combo: {
        pc: `${CURSOR_MOVE_PC}<br>Brush danger to build a chain.<br>Let it slip and you're back to x1.`,
        mobile: `${CURSOR_MOVE_MOBILE}<br>Brush danger to build a chain.<br>Let it slip and you're back to x1.`,
    },
    colorrush: {
        pc: `${CURSOR_MOVE_PC}<br>Your color rotates on a timer.<br>Grab what matches — dodge what doesn't.`,
        mobile: `${CURSOR_MOVE_MOBILE}<br>Your color rotates on a timer.<br>Grab what matches — dodge what doesn't.`,
    },
    heat: {
        pc: `${CURSOR_MOVE_PC}<br>Too still and you freeze.<br>Too frantic and you burn.`,
        mobile: `${CURSOR_MOVE_MOBILE}<br>Too still and you freeze.<br>Too frantic and you burn.`,
    },
    hunter: {
        pc: `${CURSOR_MOVE_PC}<br>Dodge the swarm.<br>Red balls hunt you down.`,
        mobile: `${CURSOR_MOVE_MOBILE}<br>Dodge the swarm.<br>Red balls hunt you down.`,
    },
    pop: {
        pc: `${CURSOR_MOVE_PC}<br>50 big balls — same size splits; large+ also split together; minis fuse.`,
        mobile: `${CURSOR_MOVE_MOBILE}<br>50 big balls — same size splits; large+ also split together; minis fuse.`,
    },
    counter: {
        pc: `${CURSOR_MOVE_PC}<br>100 numbered balls — touch them in order.<br>Five seconds per step; wrong ball ends the run.`,
        mobile: `${CURSOR_MOVE_MOBILE}<br>100 numbered balls — touch them in order.<br>Five seconds per step; wrong ball ends the run.`,
    },
    aim: {
        pc: `${CURSOR_MOVE_PC}<br>30 balls — click the golden one.<br>Two seconds per hit; clear the field for a new wave.`,
        mobile: `${CURSOR_MOVE_MOBILE}<br>30 balls — tap to shoot, drag to move.<br>Two seconds per hit; clear the field for a new wave.`,
    },
    hook: {
        pc: `${CURSOR_MOVE_PC}<br>Hold right-click and move the mouse to aim the laser (cursor stays still). Left-click to launch the hook.<br>Collect multiple normals per shot; reds only kill when they reach you on the way back.`,
        mobile: `${CURSOR_MOVE_MOBILE}<br>Use the right-side joystick to aim the laser. Tap anywhere on screen to launch the hook.<br>Collect multiple normals per shot; reds only kill when they reach you on the way back.`,
    },
    tilt: {
        pc: "Tilt your phone or tablet to move — like a ball on a tilted table.<br>Mobile / tablet with motion sensors only.",
        mobile: "Tilt the device to move — like a ball on a tilted table.<br>No finger steering; lean left/right/forward/back.",
    },
    orbit: {
        pc: "Orbit the core with A/D or ←/→, or slide/drag left-right.<br>Intercept inbound balls before they breach the center.",
        mobile: "Hold the left/right screen edges, or slide left/right.<br>Intercept inbound balls before they breach the center.",
    },
    delay: {
        pc: `${CURSOR_MOVE_PC}<br>Steer a ghost now — your real cursor follows 1 second late.`,
        mobile: `${CURSOR_MOVE_MOBILE}<br>Steer a ghost now — your real cursor follows 1 second late.`,
    },
    multiplayer: "Create or join a room.<br>Outlast friends — or survive as a team.",
    local: "Same device, split zones.<br>2–4 players on one screen.",
};

const MP_LOBBY_MODE_DESCRIPTIONS: Record<string, ModeDescCopy> = {
    normal: {
        pc: `${CURSOR_MOVE_PC}<br>Balls keep piling on.<br>Every bounce makes them angrier.`,
        mobile: `${CURSOR_MOVE_MOBILE}<br>Balls keep piling on.<br>Every bounce makes them angrier.`,
    },
    speedrun: {
        pc: `${CURSOR_MOVE_PC}<br>Lasers, walls, and a glowing finish line.`,
        mobile: `${CURSOR_MOVE_MOBILE}<br>Lasers, walls, and a glowing finish line.`,
    },
    wasd: {
        pc: "Move with your Keyboard.<br>Momentum is your ally — don't stall.",
        mobile: "Move with D-PAD.<br>Momentum is your ally — don't stall.",
    },
    sandbox: "Dial in the chaos.<br>Your settings, your experiment.",
    highway: {
        pc: `${CURSOR_MOVE_PC}<br>The road climbs forever — stay inside the frame.`,
        mobile: `${CURSOR_MOVE_MOBILE}<br>The road climbs forever — stay inside the frame.`,
    },
    snake: {
        pc: `${CURSOR_MOVE_PC}<br>Trails sync for everyone.<br>Touch one and you're out.`,
        mobile: `${CURSOR_MOVE_MOBILE}<br>Trails sync for everyone.<br>Touch one and you're out.`,
    },
};

const MP_SUBMODE_DESCRIPTIONS: Record<string, string> = {
    ffa: "Every player for themselves.<br>Fall early and watch the rest duke it out.",
    coop: "Share the pressure.<br>One fall ends it for everyone.",
};

function setModeBoxDesc(el: Element | null | undefined, html: string) {
    if (!el) return;
    if (el.innerHTML !== html) el.innerHTML = html;
}

function allModeBoxes(id: string): HTMLElement[] {
    return [...document.querySelectorAll<HTMLElement>(`#modesContainer .modeBox[data-mode-id="${id}"]`)];
}

function syncModePinIcon(box: HTMLElement, pinned: boolean) {
    let icon = box.querySelector<HTMLElement>(".modeBoxPinIcon");
    if (pinned) {
        if (!icon) {
            icon = document.createElement("span");
            icon.className = "modeBoxPinIcon";
            icon.setAttribute("aria-hidden", "true");
            icon.title = "Pinned";
            box.appendChild(icon);
        }
        box.classList.add("modeBox--pinned");
    } else {
        icon?.remove();
        box.classList.remove("modeBox--pinned");
    }
}

function findSourceModeBox(modeId: string): HTMLElement | null {
    return document.querySelector<HTMLElement>(`#modesSpGrid .modeBox[data-mode-id="${modeId}"]`);
}

function wirePinnedClone(box: HTMLElement, source: HTMLElement) {
    if (box.dataset.pinnedCloneWired === "1") return;
    box.dataset.pinnedCloneWired = "1";
    box.querySelectorAll("[id]").forEach((el) => el.removeAttribute("id"));

    const triggerSelect = () => {
        const id = source.getAttribute("data-mode-id");
        if (id) activateModeFromCard(id);
    };

    box.addEventListener("click", (e) => {
        if (e.button !== 0) return;
        if (e.target instanceof Element && e.target.closest(".modeBoxMenuBtn")) return;
        triggerSelect();
    });
    box.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            triggerSelect();
        }
    });
    box.setAttribute("role", "button");
    if (!box.hasAttribute("tabindex")) box.tabIndex = 0;
}

function layoutPinnedModes() {
    const pinnedGrid = document.getElementById("modesPinnedGrid");
    const spGrid = document.getElementById("modesSpGrid");
    const pinnedRow = document.getElementById("modesPinnedSectionRow");
    if (!pinnedGrid || !spGrid) return;

    pinnedGrid.querySelectorAll('[data-pinned-clone="true"]').forEach((el) => el.remove());

    for (const box of [...spGrid.querySelectorAll<HTMLElement>(".modeBox[data-mode-id]")]) {
        syncModePinIcon(box, false);
    }

    const pinnedIds = getPinnedModeIds();
    let pinnedCount = 0;

    for (const id of pinnedIds) {
        const source = findSourceModeBox(id);
        if (!source) continue;
        const clone = source.cloneNode(true) as HTMLElement;
        clone.setAttribute("data-pinned-clone", "true");
        syncModePinIcon(clone, true);
        wirePinnedClone(clone, source);
        pinnedGrid.appendChild(clone);
        pinnedCount++;
    }

    const showPinned = pinnedCount > 0;
    pinnedRow?.classList.toggle("hidden", !showPinned);
    pinnedGrid.classList.toggle("hidden", !showPinned);
}

export function syncModeDescriptions() {
    for (const [mode, desc] of Object.entries(MP_LOBBY_MODE_DESCRIPTIONS)) {
        setModeBoxDesc(document.querySelector(`#mpLobbyModes .mpModeCard[data-mode="${mode}"] > p`), resolveModeDesc(desc));
    }
    for (const [sub, html] of Object.entries(MP_SUBMODE_DESCRIPTIONS)) {
        setModeBoxDesc(document.querySelector(`.mpSubModeCard[data-submode="${sub}"] > p`), html);
    }
}

let modeBestProvider: ((modeId: string) => number) | null = null;

/** legacy-app registers ranking-synced best values for mode cards. */
export function setModeBestScoreProvider(fn: (modeId: string) => number) {
    modeBestProvider = fn;
}

function ensureModeBestEl(box: HTMLElement): HTMLElement {
    let el = box.querySelector<HTMLElement>(".modeBoxBest");
    if (!el) {
        el = document.createElement("p");
        el.className = "modeBoxBest";
        const title = box.querySelector("h3");
        if (title) title.insertAdjacentElement("afterend", el);
        else box.appendChild(el);
    }
    return el;
}

export function syncModeBestScores() {
    if (!modeBestProvider) return;
    for (const modeId of MODES_WITH_BEST_LINE) {
        if (getModeBestKind(modeId) === "none") continue;
        const val = modeBestProvider(modeId);
        const line = formatModeBestLine(modeId, val);
        for (const box of allModeBoxes(modeId)) {
            const el = ensureModeBestEl(box);
            if (el.textContent !== line) el.textContent = line;
        }
    }
}

export function getModeTitle(modeId: string): string {
    return MODE_DISPLAY_TITLES[modeId] ?? modeId;
}

export function getAllModeIds(): string[] {
    return MODE_DEFS.map((m) => m.id);
}

export function getLastPlayedMode(): { id: string; title: string } | null {
    const recent = readRecentPlays();
    const id = recent[0]?.id;
    if (!id || !MODE_BY_ID[id]) return null;
    return { id, title: getModeTitle(id) };
}

/** Mode with the highest local play count (for Stats → Favorite Mode). */
export function getFavoriteModeTitle(): string {
    const counts = readPlayCounts();
    let favId = "";
    let favN = 0;
    for (const [id, n] of Object.entries(counts)) {
        if (n > favN) {
            favN = n;
            favId = id;
        }
    }
    if (!favId || favN <= 0) return "—";
    return MODE_DISPLAY_TITLES[favId] ?? favId;
}

function readPlayCounts(): Record<string, number> {
    try {
        const raw = localStorage.getItem(KEY_MODE_PLAY_COUNTS);
        if (!raw) return {};
        const parsed = JSON.parse(raw) as Record<string, unknown>;
        const out: Record<string, number> = {};
        for (const [k, v] of Object.entries(parsed)) {
            const n = Math.floor(Number(v) || 0);
            if (n > 0) out[k] = n;
        }
        return out;
    } catch {
        return {};
    }
}

function writePlayCounts(counts: Record<string, number>) {
    localStorage.setItem(KEY_MODE_PLAY_COUNTS, JSON.stringify(counts));
}

type RecentModePlay = { id: string; at: number };

function readRecentPlays(): RecentModePlay[] {
    try {
        const raw = localStorage.getItem(KEY_MODE_RECENT_PLAYS);
        if (!raw) return [];
        const parsed = JSON.parse(raw) as unknown;
        if (!Array.isArray(parsed)) return [];
        const out: RecentModePlay[] = [];
        for (const row of parsed) {
            if (!row || typeof row !== "object") continue;
            const id = String((row as RecentModePlay).id || "").trim();
            const at = Number((row as RecentModePlay).at);
            if (!id || !MODE_BY_ID[id] || !Number.isFinite(at)) continue;
            out.push({ id, at });
        }
        return out;
    } catch {
        return [];
    }
}

function writeRecentPlays(list: RecentModePlay[]) {
    localStorage.setItem(KEY_MODE_RECENT_PLAYS, JSON.stringify(list.slice(0, MODE_RECENT_MAX)));
}

function recordRecentModePlay(modeId: string) {
    const now = Date.now();
    const next = [{ id: modeId, at: now }, ...readRecentPlays().filter((r) => r.id !== modeId)].slice(
        0,
        MODE_RECENT_MAX,
    );
    writeRecentPlays(next);
}

function readSavedModeSortFilter(): ModeSortFilter {
    try {
        const raw = localStorage.getItem(KEY_MODE_SORT_FILTER);
        if (raw && VALID_MODE_SORT_FILTERS.includes(raw as ModeSortFilter)) {
            return raw as ModeSortFilter;
        }
    } catch {
        /* ignore */
    }
    return "newest";
}

function saveModeSortFilter(filter: ModeSortFilter): void {
    try {
        localStorage.setItem(KEY_MODE_SORT_FILTER, filter);
    } catch {
        /* ignore */
    }
}

/** Increment local + global view count after 5 continuous seconds in a mode. */
let pendingModeViewTimer: ReturnType<typeof setTimeout> | null = null;
let pendingModeViewId: string | null = null;

export function cancelPendingModeView() {
    if (pendingModeViewTimer) {
        clearTimeout(pendingModeViewTimer);
        pendingModeViewTimer = null;
    }
    pendingModeViewId = null;
}

export function recordModePlay(modeId: string | null | undefined) {
    cancelPendingModeView();
    if (!modeId || !MODE_BY_ID[modeId]) return;
    pendingModeViewId = modeId;
    pendingModeViewTimer = setTimeout(() => {
        pendingModeViewTimer = null;
        const id = pendingModeViewId;
        pendingModeViewId = null;
        if (!id || !MODE_BY_ID[id]) return;
        recordRecentModePlay(id);
        const counts = readPlayCounts();
        counts[id] = (counts[id] || 0) + 1;
        writePlayCounts(counts);
        bumpGlobalModePlayCount(id);
    }, MODE_VIEW_MIN_MS);
}

let globalModePlayCounts: Record<string, number> | null = null;
let globalModePlayCountsPromise: Promise<void> | null = null;
const MODE_VISIT_POLL_MS = 5000;
let modeVisitPollHandle: VisibilityIntervalHandle | null = null;

function formatVisitCount(n: number): string {
    return Math.max(0, Math.floor(n)).toLocaleString("en-US");
}

function visitCountBoxes(): HTMLElement[] {
    return [...document.querySelectorAll<HTMLElement>("#modesContainer .modeBox[data-mode-id]")];
}

function isModesScreenVisible(): boolean {
    const el = document.getElementById("modeScreen");
    return !!el && !el.classList.contains("hidden");
}

function syncModeVisitCounts(boxes: HTMLElement[]): void {
    const counts = globalModePlayCounts ?? {};
    for (const box of boxes) {
        const id = box.getAttribute("data-mode-id") || "";
        if (!id) continue;
        let el = box.querySelector<HTMLElement>(".modeBoxVisitCount");
        if (!el) {
            el = document.createElement("span");
            el.className = "modeBoxVisitCount";
            box.appendChild(el);
        }
        const next = formatVisitCount(counts[id] || 0);
        if (el.textContent !== next) el.textContent = next;
        el.title = "Global views (3s in mode)";
        const label = `${next} global views`;
        if (el.getAttribute("aria-label") !== label) el.setAttribute("aria-label", label);
    }
}

async function loadGlobalModePlayCountsFromServer(): Promise<void> {
    try {
        const { counts } = await fetchModePlayCounts();
        globalModePlayCounts = counts;
    } catch {
        globalModePlayCounts = globalModePlayCounts ?? {};
    }
    if (mounted && isModesScreenVisible()) syncModeVisitCounts(visitCountBoxes());
}

function pollGlobalModePlayCounts(): void {
    if (!mounted || !isModesScreenVisible()) return;
    void fetchGlobalModePlayCounts();
}

function startModeVisitPoll(): void {
    if (modeVisitPollHandle) return;
    modeVisitPollHandle = createVisibilityInterval(pollGlobalModePlayCounts, MODE_VISIT_POLL_MS);
}

function stopModeVisitPoll(): void {
    modeVisitPollHandle?.stop();
    modeVisitPollHandle = null;
}

function syncModeVisitPollWithScreen(): void {
    if (isModesScreenVisible()) startModeVisitPoll();
    else stopModeVisitPoll();
}

function bumpGlobalModePlayCount(modeId: string) {
    if (!globalModePlayCounts) globalModePlayCounts = {};
    globalModePlayCounts[modeId] = (globalModePlayCounts[modeId] || 0) + 1;
    syncModeVisitCounts(visitCountBoxes());
    void incrementModePlayCount(modeId)
        .then((count) => {
            if (count == null || !globalModePlayCounts) return;
            globalModePlayCounts[modeId] = count;
            syncModeVisitCounts(visitCountBoxes());
        })
        .catch(() => {
            /* keep optimistic count */
        });
}

export function fetchGlobalModePlayCounts(): Promise<void> {
    if (globalModePlayCountsPromise) return globalModePlayCountsPromise;
    globalModePlayCountsPromise = loadGlobalModePlayCountsFromServer().finally(() => {
        globalModePlayCountsPromise = null;
    });
    return globalModePlayCountsPromise;
}

function normalizeSearch(q: string) {
    return q.trim().toLowerCase().replace(/\s+/g, " ");
}

function modeBoxSearchHaystack(box: HTMLElement, def: ModeDef | undefined) {
    const title = box.querySelector("h3")?.textContent?.trim() || "";
    const extra = def?.search?.join(" ") || "";
    return `${title} ${extra}`.toLowerCase().replace(/\s+/g, " ");
}

function modeMatchesSearch(box: HTMLElement, query: string) {
    const q = normalizeSearch(query);
    if (!q) return true;
    const id = box.getAttribute("data-mode-id") || "";
    const def = MODE_BY_ID[id];
    return modeBoxSearchHaystack(box, def).includes(q);
}

function sortModeIds(ids: string[], filter: ModeSortFilter, section: "sp" | "multi" = "sp") {
    const list = [...ids];
    if (section === "multi") {
        list.sort((a, b) => (MODE_BY_ID[a]?.addedOrder ?? 0) - (MODE_BY_ID[b]?.addedOrder ?? 0));
        return list;
    }
    if (filter === "newest") {
        list.sort((a, b) => (MODE_BY_ID[b]?.addedOrder ?? 0) - (MODE_BY_ID[a]?.addedOrder ?? 0));
    } else if (filter === "oldest") {
        list.sort((a, b) => (MODE_BY_ID[a]?.addedOrder ?? 0) - (MODE_BY_ID[b]?.addedOrder ?? 0));
    } else if (filter === "recent") {
        const order = new Map(readRecentPlays().map((r, i) => [r.id, i]));
        list.sort((a, b) => {
            const ia = order.has(a) ? order.get(a)! : 9999;
            const ib = order.has(b) ? order.get(b)! : 9999;
            if (ia !== ib) return ia - ib;
            return (MODE_BY_ID[b]?.addedOrder ?? 0) - (MODE_BY_ID[a]?.addedOrder ?? 0);
        });
    } else {
        const counts = globalModePlayCounts ?? readPlayCounts();
        list.sort((a, b) => {
            const diff = (counts[b] || 0) - (counts[a] || 0);
            if (diff !== 0) return diff;
            return (MODE_BY_ID[b]?.addedOrder ?? 0) - (MODE_BY_ID[a]?.addedOrder ?? 0);
        });
    }
    return list;
}

function reorderGrid(grid: HTMLElement | null, orderedIds: string[]) {
    if (!grid) return;
    const boxes = [...grid.querySelectorAll<HTMLElement>(".modeBox[data-mode-id]")];
    const byId = new Map(boxes.map((b) => [b.getAttribute("data-mode-id") || "", b]));
    for (const id of orderedIds) {
        const box = byId.get(id);
        if (box) grid.appendChild(box);
    }
}

/** Keep the search field focused while filtering (DOM updates would otherwise blur it). */
function preserveModesSearchFocus() {
    const searchInput = document.getElementById("modesSearchInput") as HTMLInputElement | null;
    if (!searchInput || document.activeElement !== searchInput) return () => {};
    const start = searchInput.selectionStart;
    const end = searchInput.selectionEnd;
    return () => {
        searchInput.focus({ preventScroll: true });
        if (start == null || end == null) return;
        try {
            searchInput.setSelectionRange(start, end);
        } catch {
            /* ignore */
        }
    };
}

let currentSort: ModeSortFilter = readSavedModeSortFilter();
let mounted = false;
let modesRandomBusy = false;
let modesRandomSfx: ((kind: ChallengeSfxKind) => void) | null = null;

export function setModesRandomSfx(fn: ((kind: ChallengeSfxKind) => void) | null): void {
    modesRandomSfx = fn;
}

function sfxModesRandom(kind: ChallengeSfxKind): void {
    modesRandomSfx?.(kind);
}

async function runModesRandomRoulette(): Promise<void> {
    if (modesRandomBusy) return;
    const ids = getAllModeIds();
    if (!ids.length) return;

    const diceBtn = document.getElementById("modesRandomDiceBtn") as HTMLButtonElement | null;
    modesRandomBusy = true;
    if (diceBtn) diceBtn.disabled = true;
    document.body.classList.add("modes-random-spin");

    try {
        const resultIndex = Math.floor(Math.random() * ids.length);
        const modeId = ids[resultIndex]!;
        const segments = ids.map((id) => ({ label: getModeTitle(id) }));

        sfxModesRandom("roulette_appear");
        await playChallengeRoulette({
            segments,
            resultIndex,
            durationMs: 5000,
            onTick: () => sfxModesRandom("roulette_tick"),
            onStop: () => sfxModesRandom("roulette_stop"),
        });

        sfxModesRandom("reveal_show");
        await showChallengeRevealText([getModeTitle(modeId)], 1800);
        sfxModesRandom("reveal_hide");

        startModeById(modeId);
    } finally {
        document.body.classList.remove("modes-random-spin");
        modesRandomBusy = false;
        if (diceBtn) diceBtn.disabled = false;
    }
}

export function refreshModesScreen() {
    const restoreSearchFocus = preserveModesSearchFocus();
    layoutPinnedModes();

    const searchInput = document.getElementById("modesSearchInput") as HTMLInputElement | null;
    const notFound = document.getElementById("modesNotFound");
    const pinnedRow = document.getElementById("modesPinnedSectionRow");
    const pinnedGrid = document.getElementById("modesPinnedGrid");
    const spTitle = document.getElementById("modesSpSectionTitle");
    const spGrid = document.getElementById("modesSpGrid");

    const query = searchInput?.value ?? "";

    const pinnedBoxes = pinnedGrid
        ? [...pinnedGrid.querySelectorAll<HTMLElement>(".modeBox[data-mode-id]")]
        : [];
    const spBoxes = spGrid
        ? [...spGrid.querySelectorAll<HTMLElement>(".modeBox[data-mode-id]")]
        : [];

    syncModeVisitCounts([...pinnedBoxes, ...spBoxes]);
    syncModePlayStreakBadges([...pinnedBoxes, ...spBoxes]);

    const visiblePinned = pinnedBoxes.filter((b) => modeMatchesSearch(b, query));
    const visibleSp = spBoxes.filter((b) => modeMatchesSearch(b, query));
    const anyVisible = visiblePinned.length > 0 || visibleSp.length > 0;
    const searchActive = normalizeSearch(query).length > 0;

    if (notFound) {
        notFound.classList.toggle("hidden", !searchActive || anyVisible);
    }

    if (spTitle) {
        spTitle.classList.remove("modesSectionTitle--searchHidden");
    }

    if (spGrid) {
        spGrid.classList.remove("hidden");
    }

    const showPinned = getPinnedModeIds().length > 0;
    pinnedRow?.classList.toggle("hidden", !showPinned || (searchActive && visiblePinned.length === 0));
    pinnedGrid?.classList.toggle("hidden", !showPinned || (searchActive && visiblePinned.length === 0));

    for (const box of pinnedBoxes) {
        box.classList.toggle("hidden", searchActive && !modeMatchesSearch(box, query));
    }

    for (const box of spBoxes) {
        box.classList.toggle("hidden", searchActive && !modeMatchesSearch(box, query));
    }

    if (!searchActive) {
        const spIds = spBoxes.map((b) => b.getAttribute("data-mode-id") || "").filter(Boolean);
        reorderGrid(spGrid, sortModeIds(spIds, currentSort, "sp"));
    }

    syncModeDescriptions();
    syncModeBestScores();
    syncModeCardMenuButtons();
    syncModeSelectionHighlight();
    void fetchGlobalModePlayCounts();
    restoreSearchFocus();
    refreshCustomScrollbars();
}

export function mountModesScreen() {
    if (mounted) return;
    mounted = true;

    const searchInput = document.getElementById("modesSearchInput") as HTMLInputElement | null;
    const sortSelect = document.getElementById("modesSortSelect") as HTMLSelectElement | null;
    const diceBtn = document.getElementById("modesRandomDiceBtn") as HTMLButtonElement | null;

    if (searchInput) {
        searchInput.addEventListener("input", () => refreshModesScreen());
    }

    if (sortSelect) {
        sortSelect.value = currentSort;
        sortSelect.addEventListener("change", () => {
            currentSort = sortSelect.value as ModeSortFilter;
            saveModeSortFilter(currentSort);
            refreshModesScreen();
        });
    }

    diceBtn?.addEventListener("click", () => {
        void runModesRandomRoulette();
    });

    let descResizeRaf = 0;
    window.addEventListener(
        "resize",
        () => {
            if (descResizeRaf) return;
            descResizeRaf = requestAnimationFrame(() => {
                descResizeRaf = 0;
                syncModeDescriptions();
            });
        },
        { passive: true },
    );

    refreshModesScreen();

    const modeEl = document.getElementById("modeScreen");
    if (modeEl) {
        syncModeVisitPollWithScreen();
        new MutationObserver(syncModeVisitPollWithScreen).observe(modeEl, {
            attributes: true,
            attributeFilter: ["class"],
        });
    }
}
